export type TDiffSegmentType = 'equal' | 'removed' | 'added';

export type TDiffSegment = {
  type: TDiffSegmentType;
  text: string;
};

const tokenize = (value: string): string[] => value.split(/(\s+)/).filter((token) => token.length > 0);

const isWhitespace = (value: string) => value.trim() === '';

// A replaced passage comes out of the walk interleaved, and a space that both
// texts share stays an equal segment in the middle of it. Regrouping each run of
// changes into one struck-through passage followed by one inserted passage is how
// a tracked change reads.
const groupChangeRuns = (segments: TDiffSegment[]): TDiffSegment[] => {
  const grouped: TDiffSegment[] = [];
  let removed = '';
  let added = '';

  const flush = () => {
    if (removed) grouped.push({ type: 'removed', text: removed });
    if (added) grouped.push({ type: 'added', text: added });
    removed = '';
    added = '';
  };

  segments.forEach((segment, index) => {
    if (segment.type === 'removed') {
      removed += segment.text;
      return;
    }
    if (segment.type === 'added') {
      added += segment.text;
      return;
    }

    // Whitespace inside a run belongs to both sides, so neither passage loses its
    // word spacing.
    const next = segments[index + 1];
    const isInsideRun = (removed || added) && next && next.type !== 'equal';
    if (isWhitespace(segment.text) && isInsideRun) {
      if (removed) removed += segment.text;
      if (added) added += segment.text;
      return;
    }

    flush();
    const previous = grouped[grouped.length - 1];
    if (previous && previous.type === 'equal') previous.text += segment.text;
    else grouped.push({ ...segment });
  });

  flush();
  return grouped;
};

// Word level diff of two short passages. Change proposals are sentences or
// paragraphs, so a plain longest-common-subsequence table is fast enough and
// keeps the marked-up result close to how a reviewer reads the text.
export const diffWords = (current: string, proposed: string): TDiffSegment[] => {
  const left = tokenize(current);
  const right = tokenize(proposed);

  const table: number[][] = Array.from({ length: left.length + 1 }, () => new Array(right.length + 1).fill(0));
  for (let i = left.length - 1; i >= 0; i -= 1) {
    for (let j = right.length - 1; j >= 0; j -= 1) {
      table[i][j] = left[i] === right[j] ? table[i + 1][j + 1] + 1 : Math.max(table[i + 1][j], table[i][j + 1]);
    }
  }

  const segments: TDiffSegment[] = [];
  const push = (type: TDiffSegmentType, text: string) => {
    const last = segments[segments.length - 1];
    if (last && last.type === type) last.text += text;
    else segments.push({ type, text });
  };

  let i = 0;
  let j = 0;
  while (i < left.length && j < right.length) {
    if (left[i] === right[j]) {
      push('equal', left[i]);
      i += 1;
      j += 1;
    } else if (table[i + 1][j] >= table[i][j + 1]) {
      push('removed', left[i]);
      i += 1;
    } else {
      push('added', right[j]);
      j += 1;
    }
  }
  while (i < left.length) {
    push('removed', left[i]);
    i += 1;
  }
  while (j < right.length) {
    push('added', right[j]);
    j += 1;
  }

  return groupChangeRuns(segments);
};
